import { DeleteOutlined, PlusOutlined, StarOutlined } from '@ant-design/icons';
import type { CreateApplyRequest, UrgentLevel } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatApiTime } from '@hq/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Card, DatePicker, Form, Input, InputNumber, Select, Space, Table, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { useAuthStore } from '../stores/auth';

interface ItemValues {
  materialId: string;
  materialName?: string;
  spec?: string;
  unitId: string;
  unitName?: string;
  qty: number;
  price: number;
}

interface FormValues {
  title?: string;
  deptId: string;
  needDate?: Dayjs;
  urgentLevel: UrgentLevel;
  spaceDesc?: string;
  remark?: string;
  items: ItemValues[];
}

/**
 * 新建申领：购物车式录入。
 * 物资从主数据（GET /api/wl/material）下拉选择，品名/规格/单位/参考价自动带出；
 * 后端仍会用主数据覆盖快照字段，因此这里不再允许手填单位与品名。
 */
export default function ApplyCreate() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const profile = useAuthStore((state) => state.profile);
  const [form] = Form.useForm<FormValues>();

  // 物资主数据：下拉选物资后自动带出名称/规格/单位/参考价（后端也会用主数据覆盖快照）
  const materials = useQuery({
    queryKey: ['material', 'options'],
    queryFn: () => hq.material.list({ size: 500 }),
    staleTime: 10 * 60 * 1000
  });

  // 科室以机构主数据为准：手填 dept_id 既猜不到内部 ID，写错还会把单据挂到别的科室
  const depts = useQuery({
    queryKey: ['apply', 'dept-options'],
    queryFn: () => hq.orgStructure.dept.list({}),
    staleTime: 10 * 60 * 1000
  });

  const materialOptions = (materials.data?.items ?? []).map((row) => ({
    value: row.id,
    label: `${row.materialName ?? row.id}${row.spec ? ` / ${row.spec}` : ''}${row.materialCode ? `（${row.materialCode}）` : ''}`,
    material: row
  }));

  const pickMaterial = (fieldName: number, materialId: string) => {
    const picked = materialOptions.find((option) => option.value === materialId)?.material;
    if (!picked) return;
    form.setFieldValue(['items', fieldName, 'materialName'], picked.materialName ?? undefined);
    form.setFieldValue(['items', fieldName, 'spec'], picked.spec ?? undefined);
    form.setFieldValue(['items', fieldName, 'unitId'], picked.baseUnitId);
    form.setFieldValue(['items', fieldName, 'unitName'], picked.unitName ?? undefined);
    form.setFieldValue(['items', fieldName, 'price'], picked.referencePrice ?? 0);
  };

  // 常购清单（hq.favorite）：一键把常用物资预置进购物车（docs/01 §6.5 小程序同款能力）
  const favorites = useQuery({
    queryKey: ['favorite', 'list'],
    queryFn: () => hq.favorite.list(),
    staleTime: 5 * 60 * 1000
  });

  const importFavorites = () => {
    const rows = favorites.data ?? [];
    if (rows.length === 0) {
      message.info('常购清单为空，可在下方明细里先建单，或到「常购清单」页维护');
      return;
    }
    const current = (form.getFieldValue('items') as ItemValues[] | undefined) ?? [];
    const existing = new Set(current.map((item) => item.materialId).filter(Boolean));
    const appended = rows
      .filter((row) => !existing.has(row.materialId))
      .map((row) => ({
        materialId: row.materialId,
        materialName: row.materialName ?? undefined,
        spec: row.spec ?? undefined,
        unitId: row.unitId,
        unitName: row.unitName ?? undefined,
        qty: row.qtyHint ?? 1,
        price: row.referencePrice ?? 0
      }));
    const kept = current.filter((item) => item.materialId);
    form.setFieldValue('items', [...kept, ...appended]);
    message.success(`已从常购清单导入 ${appended.length} 项${appended.length < rows.length ? '（重复项已跳过）' : ''}`);
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateApplyRequest) => hq.apply.create(payload),
    onSuccess: (row) => {
      message.success(`申领单已保存：${row.bizNo}`);
      navigate(`/apply/${row.id}`, { replace: true });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '提交失败，请稍后重试');
    }
  });

  const onFinish = (values: FormValues) => {
    const payload: CreateApplyRequest = {
      title: values.title ?? null,
      deptId: values.deptId,
      // 名称快照由后端按 wz-conf.org_department 回填，前端不再传递（防止乱码/伪造科室名）
      deptName: null,
      needDate: values.needDate ? formatApiTime(values.needDate.toDate()) : null,
      urgentLevel: values.urgentLevel,
      spaceDesc: values.spaceDesc ?? null,
      remark: values.remark ?? null,
      items: values.items.map((item) => ({
        materialId: item.materialId,
        materialName: item.materialName ?? null,
        spec: item.spec ?? null,
        unitId: item.unitId,
        unitName: item.unitName ?? null,
        qty: item.qty,
        price: item.price
      }))
    };
    createMutation.mutate(payload);
  };

  return (
    <Card className="page-card" title="新建申领">
      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          deptId: profile?.deptId ?? '',
          urgentLevel: 'NORMAL',
          items: [{ materialId: '', unitId: '', qty: 1, price: 0 }]
        }}
      >
        <Space size={16} wrap>
          <Form.Item name="title" label="标题" style={{ minWidth: 280 }}>
            <Input placeholder="如：11 月科室领用" />
          </Form.Item>
          <Form.Item name="deptId" label="申请科室" rules={[{ required: true, message: '请选择科室' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              placeholder="搜索并选择科室"
              loading={depts.isFetching}
              options={(depts.data ?? []).map((row) => ({ value: row.id, label: row.deptName }))}
            />
          </Form.Item>
          <Form.Item name="needDate" label="需求日期">
            {/* 需求日期不能早于今天：过去的日期会让排产/到货计划直接落在过期区间 */}
            <DatePicker showTime style={{ width: 200 }} disabledDate={(current) => current.isBefore(new Date(), 'day')} />
          </Form.Item>
          <Form.Item name="urgentLevel" label="紧急程度" rules={[{ required: true }]}>
            <Select
              style={{ width: 140 }}
              options={[
                { value: 'NORMAL', label: '普通' },
                { value: 'URGENT', label: '加急' },
                { value: 'EMERGENCY', label: '应急' }
              ]}
            />
          </Form.Item>
          <Form.Item name="spaceDesc" label="收货位置">
            <Input placeholder="楼宇/楼层/科室或地标" style={{ width: 220 }} />
          </Form.Item>
        </Space>

        <Form.Item name="remark" label="备注">
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
          <Typography.Title level={5} style={{ margin: 0 }}>
            申领明细
          </Typography.Title>
          <Button icon={<StarOutlined />} onClick={importFavorites} loading={favorites.isFetching}>
            从常购清单导入
          </Button>
        </Space>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={fields}
                columns={[
                  {
                    title: '物资',
                    render: (_, field) => (
                      <>
                        <Form.Item
                          name={[field.name, 'materialId']}
                          rules={[{ required: true, message: '请选择物资' }]}
                          style={{ margin: 0 }}
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            loading={materials.isLoading}
                            placeholder="搜索物资名称 / 编码 / 规格"
                            options={materialOptions}
                            onChange={(value: string) => pickMaterial(field.name, value)}
                          />
                        </Form.Item>
                        {/* 快照字段随物资带出，隐藏字段保证随表单提交 */}
                        <Form.Item name={[field.name, 'materialName']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'spec']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'unitId']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'unitName']} hidden>
                          <Input />
                        </Form.Item>
                      </>
                    )
                  },
                  {
                    title: '规格 / 单位',
                    width: 200,
                    render: (_, field) => {
                      const row = form.getFieldValue(['items', field.name]) as ItemValues | undefined;
                      return (
                        <Typography.Text type="secondary">
                          {row?.spec ? `${row.spec} · ` : ''}
                          {row?.unitName ?? '-'}
                        </Typography.Text>
                      );
                    }
                  },
                  {
                    title: '数量',
                    width: 140,
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, 'qty']}
                        rules={[{ required: true, message: '必填' }]}
                        style={{ margin: 0 }}
                      >
                        <InputNumber min={0.001} precision={3} style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  },
                  {
                    title: '单价',
                    width: 150,
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, 'price']}
                        rules={[{ required: true, message: '必填' }]}
                        style={{ margin: 0 }}
                      >
                        <InputNumber min={0} precision={4} style={{ width: '100%' }} />
                      </Form.Item>
                    )
                  },
                  {
                    title: '',
                    width: 56,
                    render: (_, field) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={fields.length === 1}
                        onClick={() => remove(field.name)}
                      />
                    )
                  }
                ]}
              />
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 12 }}
                onClick={() => add({ materialId: '', unitId: '', qty: 1, price: 0 })}
              >
                添加明细行
              </Button>
            </>
          )}
        </Form.List>

        <Space style={{ marginTop: 24 }}>
          <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
            保存草稿
          </Button>
          <Button onClick={() => navigate('/apply')}>返回列表</Button>
          <Typography.Text type="secondary">保存后可在详情页提交审批</Typography.Text>
        </Space>
      </Form>
    </Card>
  );
}
